import {
  type ConvictionMarketFields,
  type GetMarketsRow,
  outcomeConvictionFraction,
} from '@morfi/backend-sdk-v2/client';

/**
 * Outcome row shape the `MarketRowCard` bar grid consumes — derived from the
 * v2 `GetMarketsRow.outcomes[]` element. `share` is the outcome's normalized
 * conviction as a ~0→1 fraction (a normalized market share), computed by
 * the SDK's single `outcomeConvictionFraction` so the number is identical to
 * the chart, hero, and standings. Legacy gauge-era markets may transiently
 * show > 1.0; display must not clamp those values.
 */
export interface OutcomeRowSummary {
  /** Outcome label, displayed on the button. */
  label: string;
  /** Normalized conviction as a fraction (~0→1). A normalized market share —
   *  outcomes collectively sum to ~1.0. The market resolves when this value
   *  crosses the configured threshold θ (≤ 1.0). Legacy gauge-era markets may
   *  transiently read > 1.0 while decaying; the bar grid may clamp the FILL
   *  but never the number itself. */
  share: number;
  /** Outcome's canonical backend `outcomeIndex`, independent of array order. */
  originalIndex: number;
  /** Effective market cap D6-encoded string — drives the hover "€X Mcap" row. */
  effectiveMarketCapD6: string;
  /** Optional decorative outcome image URL (spec 062). */
  imageUrl?: string | null;
  /** NSFW flag for `imageUrl` (spec 071). */
  imageNsfw?: boolean;
}

type V2Outcome = NonNullable<GetMarketsRow['outcomes']>[number];

function buildSummary(o: V2Outcome, share: number): OutcomeRowSummary {
  return {
    label: o.label,
    share,
    originalIndex: o.outcomeIndex,
    effectiveMarketCapD6: o.effectiveMarketCapD6,
    imageUrl: o.imageUrl,
    imageNsfw: o.imageNsfw,
  };
}

/**
 * Translate v2 `GetMarketsRow.outcomes[]` into the `OutcomeRowSummary` shape
 * the row card + sparkline grid consume. `share` is the outcome's normalized
 * conviction via the SDK's `outcomeConvictionFraction` — under the normalized
 * model the targets sum to ~1e9 PPB so values are ~0→1 market shares. Do NOT
 * re-normalise by the cross-outcome sum; the raw PPB/1e9 value IS the share
 * (within dust). Legacy gauge-era markets may transiently exceed 1.0; display
 * must pass those values through unclamped.
 *
 * `market` carries `status`/`resolvedAt` so a resolved/graduated market pins to
 * the conviction AT RESOLUTION (the SDK projector has already frozen
 * `conviction` to that value on the feed hook). Omit it for live-only callers.
 */
export function buildOutcomeRowSummaries(
  outcomes: readonly V2Outcome[],
  market: ConvictionMarketFields = {},
): OutcomeRowSummary[] {
  return outcomes.map((o) => buildSummary(o, outcomeConvictionFraction(o, market)));
}

/**
 * True when two summaries are field-for-field equal — used by
 * {@link buildOutcomeRowSummariesStable} to decide whether the previous
 * object reference can be reused.
 */
function summariesEqual(a: OutcomeRowSummary, b: OutcomeRowSummary): boolean {
  return (
    a.label === b.label &&
    a.share === b.share &&
    a.originalIndex === b.originalIndex &&
    a.effectiveMarketCapD6 === b.effectiveMarketCapD6 &&
    (a.imageUrl ?? null) === (b.imageUrl ?? null) &&
    (a.imageNsfw ?? false) === (b.imageNsfw ?? false)
  );
}

/**
 * Re-render-isolating variant of {@link buildOutcomeRowSummaries}.
 *
 * The SDK realtime reducer rebuilds the WHOLE `outcomes` array (every
 * element gets a fresh object reference) on every trade — see
 * `markets.spec.gen.ts`, the `outcomes.conviction` zip spreads `{...o}`
 * for every index, traded or not. A plain `buildOutcomeRowSummaries` then
 * mints a fresh `OutcomeRowSummary` for every outcome on every tick, so
 * the bar grid's per-outcome leaves can never bail out under the React
 * Compiler — even the share-independent parts (outcome image, live-trades
 * overlay, label button) re-render on a trade that didn't touch them.
 *
 * This variant diffs each freshly-built summary against the matching entry
 * in `prev` (by `originalIndex`) and REUSES the previous object reference
 * when the two are field-equal. Net effect: after a trade on outcome `i`,
 * only the summary objects whose derived values actually changed get new
 * references. A leaf component keyed by `originalIndex` that receives its
 * own summary as a prop then keeps a stable prop — and the React Compiler
 * bails its render — whenever that outcome's values are unchanged.
 *
 * `share` is raw per-outcome conviction, so a trade on outcome `i` only moves
 * `i`'s value (and any outcome the trade re-targets); the share-INDEPENDENT
 * subtree of an untouched outcome (image decode, overlay subscription render,
 * label button) keeps its previous reference and no longer churns.
 *
 * Pass the previous result back in on each call (`useRef`-held by the
 * caller). On first call pass `undefined` / `[]`.
 */
export function buildOutcomeRowSummariesStable(
  outcomes: readonly V2Outcome[],
  prev: readonly OutcomeRowSummary[] | undefined,
  market: ConvictionMarketFields = {},
): OutcomeRowSummary[] {
  const fresh = buildOutcomeRowSummaries(outcomes, market);
  if (prev === undefined || prev.length === 0) {
    return fresh;
  }
  const prevByIndex = new Map<number, OutcomeRowSummary>();
  for (const p of prev) {
    prevByIndex.set(p.originalIndex, p);
  }
  return fresh.map((next) => {
    const previous = prevByIndex.get(next.originalIndex);
    return previous !== undefined && summariesEqual(previous, next) ? previous : next;
  });
}
